import {
  startTransition,
  useCallback,
  useDeferredValue,
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type FC,
  type SetStateAction,
} from 'react';
import { Trans, useTranslation } from 'react-i18next';
import isEqual from 'lodash.isequal';
import { zodResolver } from '@hookform/resolvers/zod';
import { useFieldArray, useForm, useWatch } from 'react-hook-form';
import { Button, Flex, Form, Tabs, Typography, type TabsProps } from 'antd';
import { useNavigate } from 'react-router-dom';

import { useFormSchema } from '@modules/ai-agent/hooks/use-form-schema';
import type { UpdateFormValues } from '@modules/ai-agent/lib/types';

import { useGetAgentInfoAgentInfoGetQuery } from '@shared/api';
import { useUpdateAgent } from '@shared/hooks/use-update-ai-agent';
import { useDeleteAgent } from '@shared/hooks/use-delete-ai-agent';
import { useGetAgents } from '@shared/hooks/use-get-agents';
import { store, useAppDispatch } from '@shared/redux';
import {
  channelFormDraftsActions,
  channelFormDraftsSelectors,
} from '@shared/redux/ducks/channel-form-drafts';
import { useGlobalConfirmModalActions } from '@shared/redux/ducks/global-confirm-modal';
import { Routes } from '@shared/constants/routes';

import { LoadingState } from '@shared/ui/loading-state/loading-state';
import { FetchingErrorState } from '@shared/ui/fetching-error-state/fetching-error-state';

import { BasicFields } from './ui/basic-fields/basic-fields';
import { DialogSteps } from './ui/dialog-steps/dialog-steps';
import { Recommendations } from './ui/recommendations/recommendations';
import { ContactFormPrompts } from './ui/contact-form-prompts/contact-form-prompts';
import { AdditionalInstructions } from './ui/additional-instructions/additional-instructions';
import { RelatedAgents } from './ui/related-agents/related-agents';
import { KnowledgeBaseSection } from './ui/knowledge-base/knowledge-base';
import { IntegrationsSection } from './ui/integrations/integrations';
import { RequiredFields } from './ui/required-fields/required-fields';
import { AgentTestChat } from './ui/agent-test-chat/agent-test-chat';
import { BackupsSection } from './ui/backups/backups';
import {
  buildUpdateAgentPatch,
  createEmptyAgentLink,
  createEmptyStage,
  mapAgentInfoToFormValues,
  normalizePromptFormValues,
} from './lib/utils';

import styles from './prompt.module.scss';
import { Text } from '@shared/ui/antd';

interface PromptProps {
  agentId: number;
}

export const Prompt: FC<PromptProps> = ({ agentId }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('translation', { keyPrefix: 'prompt' });
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const { Text: AntdText } = Typography;
  const navigate = useNavigate();
  const formSchema = useFormSchema(agentId);

  const { updateAgent, isLoading } = useUpdateAgent();
  const { deleteAgent } = useDeleteAgent();
  const { agents } = useGetAgents();
  const { openConfirmModal } = useGlobalConfirmModalActions();
  const promptDraftKey = useMemo(() => `ai-agent:prompt:${agentId}`, [agentId]);
  const savedPromptDraft = useMemo(
    () =>
      channelFormDraftsSelectors.selectDraftByKey(promptDraftKey)(store.getState()) as
        | Partial<UpdateFormValues>
        | undefined,
    [promptDraftKey],
  );
  const normalizedSavedDraft = useMemo<UpdateFormValues | null>(() => {
    if (!savedPromptDraft) return null;

    return normalizePromptFormValues(savedPromptDraft);
  }, [savedPromptDraft]);

  const {
    data: selectedAgent,
    isLoading: isAgentLoading,
    isError: isAgentError,
    refetch,
  } = useGetAgentInfoAgentInfoGetQuery(
    { agentId },
    {
      skip: !agentId,
    },
  );
  const normalizedApiValues = useMemo<UpdateFormValues | null>(() => {
    if (!selectedAgent) return null;

    return mapAgentInfoToFormValues(selectedAgent);
  }, [selectedAgent]);

  const {
    handleSubmit,
    control,
    reset,
    clearErrors,
    setValue,
    getValues,
    watch,
    formState: { errors, isSubmitting, isValid, dirtyFields },
  } = useForm<UpdateFormValues>({
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(formSchema),
    defaultValues: normalizePromptFormValues(normalizedSavedDraft),
  });

  const [ruleCount, setRuleCount] = useState<number>(1);
  const [exceptionCount, setExceptionCount] = useState<number>(1);
  const [contactFormPromptCount, setContactFormPromptCount] = useState<number>(0);
  const [activeTab, setActiveTab] = useState<string>('general');
  const hasInitializedFormRef = useRef(false);
  const initialSavedDraftRef = useRef<UpdateFormValues | null>(null);
  const draftSyncTimeoutRef = useRef<number | null>(null);
  const [hasChangesComparedToApi, setHasChangesComparedToApi] = useState(false);
  const watchedName = useWatch({ control, name: 'name' }) ?? '';
  const deferredAgentName = useDeferredValue(
    watchedName.trim() || selectedAgent?.name?.trim() || null,
  );

  const {
    fields: stageFields,
    append: appendStage,
    remove: removeStage,
    move: moveStage,
  } = useFieldArray({
    control,
    name: 'stages',
  });

  const {
    fields: agentLinkFields,
    append: appendAgentLink,
    remove: removeAgentLink,
  } = useFieldArray({
    control,
    name: 'agent_links',
  });

  const dialogStepsPlaceholders = [t('dialogStepsPlaceholder')];
  const linkedAgentPlaceholders = [t('linkedAgents.promptPlaceholder')];
  const contactFormPromptPlaceholders = [t('contactForm.placeholder')];
  const rulePlaceholders = [t('rulesPlaceholder')];
  const exceptionPlaceholders = [t('exceptionsPlaceholder')];

  const handleAddField = useCallback(
    (
      fieldName: 'rules' | 'exceptions' | 'show_form_prompts',
      setCount: Dispatch<SetStateAction<number>>,
    ) => {
      const currentValues = (getValues(fieldName) ?? []) as string[];
      const nextValues = [...currentValues, ''];
      setValue(fieldName, nextValues, { shouldValidate: true, shouldDirty: true });
      setCount(nextValues.length);
    },
    [getValues, setValue],
  );

  const handleRemoveField = useCallback(
    (
      fieldName: 'rules' | 'exceptions' | 'show_form_prompts',
      index: number,
      min: number,
      setCount: Dispatch<SetStateAction<number>>,
    ) => {
      const currentValues = (getValues(fieldName) ?? []) as string[];
      const nextValues = [...currentValues];
      nextValues.splice(index, 1);

      while (nextValues.length < min) nextValues.push('');

      setValue(fieldName, nextValues, { shouldValidate: true, shouldDirty: true });
      setCount(nextValues.length);
    },
    [getValues, setValue],
  );

  useEffect(() => {
    hasInitializedFormRef.current = false;
    initialSavedDraftRef.current = null;
    setHasChangesComparedToApi(false);

    if (draftSyncTimeoutRef.current) {
      window.clearTimeout(draftSyncTimeoutRef.current);
      draftSyncTimeoutRef.current = null;
    }
  }, [agentId]);

  useEffect(() => {
    if (hasInitializedFormRef.current) return;
    if (!normalizedSavedDraft) return;
    initialSavedDraftRef.current = normalizedSavedDraft;
  }, [normalizedSavedDraft]);

  useEffect(() => {
    if (!selectedAgent) return;
    if (!normalizedApiValues) return;

    if (!hasInitializedFormRef.current) {
      const nextValues = normalizePromptFormValues(
        initialSavedDraftRef.current ?? normalizedApiValues,
      );

      reset(nextValues);

      setRuleCount((nextValues.rules ?? []).length || 1);
      setExceptionCount((nextValues.exceptions ?? []).length || 1);
      setContactFormPromptCount((nextValues.show_form_prompts ?? []).length || 0);
      setHasChangesComparedToApi(!isEqual(nextValues, normalizedApiValues));
      hasInitializedFormRef.current = true;
      return;
    }

    if (hasChangesComparedToApi) return;

    const nextValues = normalizePromptFormValues(normalizedApiValues);

    reset(nextValues);

    setRuleCount((nextValues.rules ?? []).length || 1);
    setExceptionCount((nextValues.exceptions ?? []).length || 1);
    setContactFormPromptCount((nextValues.show_form_prompts ?? []).length || 0);
    setHasChangesComparedToApi(false);
  }, [hasChangesComparedToApi, normalizedApiValues, reset, selectedAgent]);

  useEffect(() => {
    if (!normalizedApiValues) return;
    const subscription = watch((values) => {
      const nextValues = normalizePromptFormValues(values as Partial<UpdateFormValues>);
      const nextHasChanges = !isEqual(nextValues, normalizedApiValues);

      startTransition(() => {
        setHasChangesComparedToApi(nextHasChanges);
      });

      if (draftSyncTimeoutRef.current) {
        window.clearTimeout(draftSyncTimeoutRef.current);
        draftSyncTimeoutRef.current = null;
      }

      if (!nextHasChanges) {
        dispatch(channelFormDraftsActions.removeDraft(promptDraftKey));
        return;
      }

      draftSyncTimeoutRef.current = window.setTimeout(() => {
        dispatch(
          channelFormDraftsActions.upsertDraft({
            key: promptDraftKey,
            values: nextValues as Record<string, unknown>,
          }),
        );
      }, 250);
    });

    return () => {
      subscription.unsubscribe();

      if (draftSyncTimeoutRef.current) {
        window.clearTimeout(draftSyncTimeoutRef.current);
        draftSyncTimeoutRef.current = null;
      }
    };
  }, [dispatch, normalizedApiValues, promptDraftKey, watch]);

  const ruleFields = useMemo(
    () => Array.from({ length: ruleCount }, (_, index) => ({ id: `rule-${index}` })),
    [ruleCount],
  );

  const stages = useWatch({ control, name: 'stages' }) ?? [];
  const hasEmptyStageField = useMemo(
    () =>
      stages.some((stage) => {
        const fieldId = Number(stage?.agent_field_id ?? 0);
        return fieldId <= 0 || !stage?.prompt?.trim();
      }),
    [stages],
  );

  const agentLinks = useWatch({ control, name: 'agent_links' }) ?? [];
  const hasEmptyAgentLink = useMemo(
    () =>
      agentLinks.some((link) => {
        const targetAgentId = Number(link?.target_agent_id ?? 0);
        return targetAgentId <= 0 || !link?.prompt?.trim();
      }),
    [agentLinks],
  );

  const exceptionFields = useMemo(
    () => Array.from({ length: exceptionCount }, (_, index) => ({ id: `exception-${index}` })),
    [exceptionCount],
  );

  const contactFormPromptFields = useMemo(
    () =>
      Array.from({ length: contactFormPromptCount }, (_, index) => ({
        id: `contact-form-prompt-${index}`,
      })),
    [contactFormPromptCount],
  );

  const showFormPrompts = useWatch({ control, name: 'show_form_prompts' }) ?? [];
  const hasEmptyContactFormPrompt = useMemo(
    () => showFormPrompts.some((prompt) => !prompt?.trim()),
    [showFormPrompts],
  );

  const handleAddStage = useCallback(() => {
    appendStage(createEmptyStage(stageFields.length), { shouldFocus: false });
  }, [appendStage, stageFields.length]);

  const handleRemoveStage = useCallback(
    (index: number) => {
      removeStage(index);
    },
    [removeStage],
  );

  const handleAddAgentLink = useCallback(() => {
    appendAgentLink(createEmptyAgentLink(), { shouldFocus: false });
  }, [appendAgentLink]);

  const handleRemoveAgentLink = useCallback(
    (index: number) => {
      removeAgentLink(index);
    },
    [removeAgentLink],
  );

  const handleRestoreBackup = useCallback(async () => {
    const restoredAgentResponse = await refetch();

    if (!('data' in restoredAgentResponse) || !restoredAgentResponse.data) return;

    const nextValues = normalizePromptFormValues(
      mapAgentInfoToFormValues(restoredAgentResponse.data),
    );

    reset(nextValues);
    clearErrors();
    setRuleCount((nextValues.rules ?? []).length || 1);
    setExceptionCount((nextValues.exceptions ?? []).length || 1);
    setContactFormPromptCount((nextValues.show_form_prompts ?? []).length || 0);
    setHasChangesComparedToApi(false);
  }, [clearErrors, refetch, reset]);

  const handleSubmitForm = async (values: UpdateFormValues) => {
    const patch: ReturnType<typeof buildUpdateAgentPatch> = normalizedApiValues
      ? (() => {
          const nextPatch: ReturnType<typeof buildUpdateAgentPatch> = {};

          if (values.name !== normalizedApiValues.name) nextPatch.name = values.name;
          if (values.role !== normalizedApiValues.role) nextPatch.role = values.role;
          if (values.style !== normalizedApiValues.style) nextPatch.style = values.style;
          if (values.recommendations !== normalizedApiValues.recommendations) {
            nextPatch.recommendations = values.recommendations;
          }
          if (values.charge_method !== normalizedApiValues.charge_method) {
            nextPatch.charge_method = values.charge_method;
          }

          if (!isEqual(values.rules, normalizedApiValues.rules)) nextPatch.rules = values.rules;
          if (!isEqual(values.exceptions, normalizedApiValues.exceptions)) {
            nextPatch.exceptions = values.exceptions;
          }
          if (!isEqual(values.show_form_prompts, normalizedApiValues.show_form_prompts)) {
            nextPatch.show_form_prompts = values.show_form_prompts;
          }

          if (!isEqual(values.stages, normalizedApiValues.stages)) {
            nextPatch.stages = (values.stages ?? []).map((stage, index) => ({
              order: index,
              agent_field_id: stage.agent_field_id,
              prompt: stage.prompt,
            }));
          }

          if (!isEqual(values.agent_links, normalizedApiValues.agent_links)) {
            nextPatch.agent_links = (values.agent_links ?? []).map((link) => ({
              target_agent_id: link.target_agent_id,
              prompt: link.prompt,
            }));
          }

          return nextPatch;
        })()
      : buildUpdateAgentPatch(values, dirtyFields);

    if (Object.keys(patch).length === 0) {
      dispatch(channelFormDraftsActions.removeDraft(promptDraftKey));
      setHasChangesComparedToApi(false);
      return;
    }

    const updatedAgent = await updateAgent(agentId, patch);

    const nextStages = (updatedAgent.agent_stages ?? [])
      .slice()
      .sort((a, b) => a.order - b.order)
      .map((stage, index) => ({
        order: index,
        agent_field_id: stage.agent_field_id,
        prompt: stage.prompt,
      }));

    const nextAgentLinks = (updatedAgent.agent_links ?? []).map((link) => ({
      target_agent_id: link.target_agent_id,
      prompt: link.prompt,
    }));

    const nextShowFormPrompts = (updatedAgent.show_form_prompts ?? []).map(
      (prompt) => prompt ?? '',
    );
    const nextRules = (updatedAgent.rules ?? []).length ? (updatedAgent.rules ?? []) : [''];
    const nextExceptions = (updatedAgent.exceptions ?? []).length
      ? (updatedAgent.exceptions ?? [])
      : [''];

    const nextValues = normalizePromptFormValues({
      name: updatedAgent.name ?? '',
      role: updatedAgent.role ?? '',
      style: updatedAgent.style ?? '',
      recommendations: updatedAgent.recommendations ?? '',
      charge_method: updatedAgent.charge_method ?? 'lead',
      stages: nextStages.length ? nextStages : [],
      agent_links: nextAgentLinks,
      show_form_prompts: nextShowFormPrompts,
      rules: nextRules,
      exceptions: nextExceptions,
    });

    reset(nextValues);

    setRuleCount(nextValues.rules.length);
    setExceptionCount(nextValues.exceptions.length);
    setContactFormPromptCount(nextValues.show_form_prompts.length);
    dispatch(channelFormDraftsActions.removeDraft(promptDraftKey));
    setHasChangesComparedToApi(false);
  };

  const handleOpenDeleteModal = useCallback(() => {
    openConfirmModal({
      title: tCommon('sidebar.deleteModal.title'),
      text: (
        <Trans
          t={tCommon}
          i18nKey="sidebar.deleteModal.text"
          values={{
            agent: selectedAgent?.name ?? '',
            interpolation: { escapeValue: false },
          }}
          components={{ bold: <AntdText strong /> }}
        />
      ),
      onConfirm: () => {
        dispatch(channelFormDraftsActions.removeDraft(promptDraftKey));
        deleteAgent(agentId).then(() => {
          navigate(Routes.analytics.root);
        });
      },
    });
  }, [
    agentId,
    deleteAgent,
    dispatch,
    navigate,
    openConfirmModal,
    promptDraftKey,
    selectedAgent?.name,
    tCommon,
    AntdText,
  ]);

  const stageFieldOptions = useMemo(() => {
    const fields = selectedAgent?.agent_fields ?? [];

    return fields.map((f) => ({
      value: f.id,
      label: `${f.name}${f.is_required ? ' *' : ''}${!f.is_active ? t('fields.inactiveSuffix') : ''}`,
    }));
  }, [selectedAgent?.agent_fields, t]);

  const linkedAgentOptions = useMemo(
    () =>
      (agents ?? [])
        .filter((agent) => agent.id !== agentId)
        .map((agent) => ({
          value: agent.id,
          label: agent.name,
        })),
    [agentId, agents],
  );

  const tabItems: TabsProps['items'] = useMemo(
    () => [
      {
        key: 'general',
        label: t('tabs.general'),
        children: (
          <Flex vertical gap={12}>
            <BasicFields control={control} errors={errors} isLoading={isLoading} t={t} />

            <BackupsSection agentId={agentId} onRestoreBackup={handleRestoreBackup} />

            <Flex vertical gap={8} className={styles.deleteSection}>
              <Text variant="md" fontWeight={500} className={styles.deleteTitle}>
                {t('deleteAgent.title')}
              </Text>

              <Flex vertical gap={4}>
                <Text variant="sm" className={styles.deleteDescription}>
                  {t('deleteAgent.description')}
                </Text>
                <Text variant="sm" className={styles.deleteDescription}>
                  {t('deleteAgent.warning')}
                </Text>
              </Flex>

              <Button
                type="primary"
                danger
                className={styles.deleteButton}
                onClick={handleOpenDeleteModal}
                size="large"
              >
                {t('deleteAgent.button')}
              </Button>
            </Flex>
          </Flex>
        ),
      },
      {
        key: 'client',
        label: t('tabs.client'),
        children: (
          <Flex vertical gap={12}>
            <RequiredFields
              agentId={agentId}
              agentFields={selectedAgent?.agent_fields || []}
              t={t}
            />

            <AdditionalInstructions
              ruleFields={ruleFields}
              exceptionFields={exceptionFields}
              control={control}
              errors={errors}
              isLoading={isLoading}
              onAddRule={() => handleAddField('rules', setRuleCount)}
              onRemoveRule={(index) => handleRemoveField('rules', index, 1, setRuleCount)}
              onAddException={() => handleAddField('exceptions', setExceptionCount)}
              onRemoveException={(index) =>
                handleRemoveField('exceptions', index, 1, setExceptionCount)
              }
              rulePlaceholders={rulePlaceholders}
              exceptionPlaceholders={exceptionPlaceholders}
              t={t}
            />

            <DialogSteps
              stageFields={stageFields}
              control={control}
              errors={errors}
              isLoading={isLoading}
              dialogStepsPlaceholders={dialogStepsPlaceholders}
              stageFieldOptions={stageFieldOptions}
              onAddStage={handleAddStage}
              onRemoveStage={handleRemoveStage}
              onMoveStage={moveStage}
              t={t}
            />

            <Recommendations control={control} errors={errors} isLoading={isLoading} t={t} />

            <ContactFormPrompts
              promptFields={contactFormPromptFields}
              control={control}
              errors={errors}
              isLoading={isLoading}
              placeholders={contactFormPromptPlaceholders}
              onAddPrompt={() => handleAddField('show_form_prompts', setContactFormPromptCount)}
              onRemovePrompt={(index) =>
                handleRemoveField('show_form_prompts', index, 0, setContactFormPromptCount)
              }
              t={t}
            />
          </Flex>
        ),
      },
      {
        key: 'knowledge',
        label: t('tabs.knowledge'),
        children: (
          <KnowledgeBaseSection
            agentId={agentId}
            selectedKnowledgeIds={selectedAgent?.agent_knowledge ?? []}
          />
        ),
      },
      {
        key: 'integrations',
        label: t('tabs.integrations'),
        children: <IntegrationsSection agentId={agentId} />,
      },
      {
        key: 'relatedAgents',
        label: t('tabs.relatedAgents'),
        children: (
          <RelatedAgents
            agentLinkFields={agentLinkFields}
            control={control}
            errors={errors}
            isLoading={isLoading}
            promptPlaceholders={linkedAgentPlaceholders}
            agentOptions={linkedAgentOptions}
            onAddAgentLink={handleAddAgentLink}
            onRemoveAgentLink={handleRemoveAgentLink}
            t={t}
          />
        ),
      },
    ],
    [
      agentId,
      agentLinkFields,
      contactFormPromptFields,
      contactFormPromptPlaceholders,
      control,
      dialogStepsPlaceholders,
      errors,
      exceptionFields,
      exceptionPlaceholders,
      handleAddField,
      handleAddAgentLink,
      handleAddStage,
      handleOpenDeleteModal,
      handleRemoveField,
      handleRemoveAgentLink,
      handleRemoveStage,
      handleRestoreBackup,
      isLoading,
      linkedAgentOptions,
      linkedAgentPlaceholders,
      moveStage,
      ruleFields,
      rulePlaceholders,
      selectedAgent?.agent_fields,
      selectedAgent?.agent_knowledge,
      stageFieldOptions,
      stageFields,
      t,
    ],
  );

  return (
    <Flex style={{ flex: 1, minHeight: 0, width: '100%' }}>
      {isAgentLoading && <LoadingState />}
      {isAgentError && <FetchingErrorState refetch={refetch} />}

      <div className={styles.root}>
        {!isAgentLoading && !isAgentError && selectedAgent && (
          <div className={styles.layout}>
            <div className={styles.mainColumn}>
              <Form
                onChange={() => clearErrors('root')}
                onFinish={handleSubmit(handleSubmitForm)}
                className={styles.form}
                layout="vertical"
              >
                <Flex vertical gap={20} className={styles.container}>
                  <Tabs
                    className={styles.tabs}
                    activeKey={activeTab}
                    onChange={setActiveTab}
                    items={tabItems}
                    more={{ trigger: 'hover' }}
                    tabBarExtraContent={
                      <Button
                        className={styles.tabsSaveButton}
                        type="primary"
                        htmlType="submit"
                        loading={isSubmitting || isLoading}
                        disabled={
                          !isValid ||
                          isSubmitting ||
                          !hasChangesComparedToApi ||
                          isLoading ||
                          hasEmptyStageField ||
                          hasEmptyAgentLink ||
                          hasEmptyContactFormPrompt
                        }
                        size="large"
                      >
                        {t('submit')}
                      </Button>
                    }
                  />
                </Flex>
              </Form>
            </div>

            <div className={styles.chatColumn}>
              <AgentTestChat
                agentId={agentId}
                agentName={deferredAgentName}
                agentFields={selectedAgent?.agent_fields}
                availableAgents={agents}
              />
            </div>
          </div>
        )}
      </div>
    </Flex>
  );
};
